import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Box, Alert, Typography, Grid2 as Grid, Paper, List, ListItem, Chip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useRecentFinanceItems } from '../../hooks/useRecentFinanceItems';

const TYPE_HELP = {
  1: 'Soma ao valor bruto do atendimento (ex.: cobrança extra, serviço adicional, acréscimo).',
  2: 'Subtrai do bruto para compor o valor pago pelo cliente (ex.: promoção, fidelidade).',
  3: 'Subtrai do valor do cliente. Não é visível ao cliente. Representa despesas do estabelecimento (ex.: material, taxa da maquininha).',
  4: 'Subtrai do valor do cliente. Representa o pagamento ao profissional/terceiros. Compõe o cálculo do lucro líquido.',
};

const TYPE_LABELS = { 1: 'Adicional', 2: 'Desconto', 3: 'Custo', 4: 'Comissão' };
const TYPE_COLORS = { 1: 'success', 2: 'error', 3: 'warning', 4: 'info' };

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

export default function FinanceAddItemDialog({ open, onClose, onSubmit, initialItem, itemsCount = 0 }) {
  const [type, setType] = useState(1);
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [valueType, setValueType] = useState(1);
  const [errors, setErrors] = useState({});

  const { items: recentItems, push: pushRecent } = useRecentFinanceItems();

  useEffect(() => {
    if (initialItem) {
      setType(initialItem.type);
      setDescription(initialItem.description);
      setValue(initialItem.value.toString());
      setValueType(initialItem.valueType);
    } else {
      setType(1);
      setDescription('');
      setValue('');
      setValueType(1);
      setErrors({});
    }
  }, [initialItem, open]);

  const validate = () => {
    const newErrors = {};
    if (!description.trim()) newErrors.description = 'Descrição obrigatória';
    if (!value || parseFloat(value) <= 0) newErrors.value = 'Valor deve ser > 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    pushRecent({ type, description: description.trim(), value: parseFloat(value), valueType });
    onSubmit({
      id: initialItem?.id ?? null,
      type,
      description: description.trim(),
      value: parseFloat(value),
      valueType,
      displayOrder: itemsCount,
    });
    onClose();
  };

  const handleUseRecent = (item) => {
    setType(item.type);
    setDescription(item.description);
    setValue(item.value.toString());
    setValueType(item.valueType);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Adicionar Item</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Coluna esquerda: campos do formulário */}
          <Grid size={{ xs: 12, md: 7 }}>
            <TextField
              select
              fullWidth
              label="Tipo"
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value={1}>Adicional</MenuItem>
              <MenuItem value={2}>Desconto</MenuItem>
              <MenuItem value={3}>Custo</MenuItem>
              <MenuItem value={4}>Comissão</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -1 }}>
              {TYPE_HELP[type]}
            </Typography>

            <TextField
              fullWidth
              label="Descrição"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={!!errors.description}
              helperText={errors.description}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Valor"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              error={!!errors.value}
              helperText={errors.value}
              inputProps={{ step: '0.01' }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                fullWidth
                exclusive
                value={valueType}
                onChange={(e, v) => v !== null && setValueType(v)}
              >
                <ToggleButton value={1}>R$</ToggleButton>
                <ToggleButton value={2}>%</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {valueType === 2 && value && parseFloat(value) > 100 && (
              <Alert severity="warning">Percentual > 100% pode resultar em valores negativos</Alert>
            )}
          </Grid>

          {/* Coluna direita: itens recentes */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Últimos itens usados
              </Typography>
              {recentItems.length === 0 ? (
                <Typography variant="caption" color="text.disabled">
                  Nenhum item recente
                </Typography>
              ) : (
                <List dense disablePadding>
                  {recentItems.map((item, idx) => (
                    <ListItem
                      key={idx}
                      disablePadding
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1, pb: 1, borderBottom: idx < recentItems.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', flexWrap: 'wrap', mb: 0.5 }}>
                        <Chip label={TYPE_LABELS[item.type]} color={TYPE_COLORS[item.type]} size="small" />
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {item.valueType === 2 ? `${item.value}%` : fmt(item.value)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                        {item.description}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon fontSize="inherit" />}
                        onClick={() => handleUseRecent(item)}
                        sx={{ minWidth: 0, px: 1, py: 0.25 }}
                      >
                        Usar
                      </Button>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit}>Adicionar</Button>
      </DialogActions>
    </Dialog>
  );
}
